<script setup lang="ts">
import { VaultAssetInstallationEntityType } from '~/codegen/graphql'
import useSkillNavigationContext from '~/composables/use-skill-navigation-context'
import { GetTeamForNavigationDocument } from './CurrentLocationTeam.generated'

const props = defineProps<{
  teamId: string
}>()

const { canChangeOrgConfig } = useCurrentUser()

const route = useRoute()
const { context: skillNavContext } = useSkillNavigationContext()

const { data: teamData } = useQuery({
  query: GetTeamForNavigationDocument,
  variables: computed(() => ({
    id: props.teamId,
  })),
})

const team = computed(() => teamData.value?.organization?.team)

const teamSettingsModalOpen = ref(false)

function dropdownItems() {
  return [
    [
      {
        label: 'Team settings',
        icon: 'light:gear',
        click: () => {
          teamSettingsModalOpen.value = true
        },
      },
    ],
  ]
}

function onTeamHome() {
  if (route.name.toString() === 'teams-teamId') return true
  // Also highlight when viewing a skill installed only to this team
  if (
    skillNavContext.value.entityType === VaultAssetInstallationEntityType.Team &&
    skillNavContext.value.entityId === props.teamId
  )
    return true
  return false
}
</script>

<template>
  <SettingsOrgTeamModal :id="teamId" v-model="teamSettingsModalOpen" mode="edit" />

  <div v-if="team" class="flex flex-col pl-2">
    <UButton
      color="white-nav"
      variant="ghost"
      active-class="dark:bg-gray-800 bg-gray-200"
      class="group/navItem relative mb-px flex flex-1 justify-between px-2"
      :class="onTeamHome() ? ['bg-blue-80', 'dark:bg-gray-900'] : []"
      @click="navigateTo({ name: 'teams-teamId', params: { teamId } })"
    >
      <div v-if="onTeamHome()" class="absolute inset-y-1.5 left-[-5px] w-[3px] rounded-md bg-blue-600" />
      <UTooltip
        :text="team.name"
        :prevent="team.name.length < 23"
        :popper="{ placement: 'top', offsetDistance: 14 }"
        :open-delay="500"
        class="flex flex-1 items-center gap-x-2 truncate"
      >
        <UIcon name="light:users" class="size-4 shrink-0 grow-0 text-gray-500 dark:text-gray-400" />
        <span class="truncate text-left text-nav font-light text-gray-900 dark:text-white">
          {{ team.name }}
        </span>
      </UTooltip>
      <OptionsDropdown
        v-if="canChangeOrgConfig"
        :popper="{ offsetDistance: 12, offsetSkid: 6 }"
        :items="dropdownItems()"
        :ui="{ width: 'w-48' }"
        name="team"
        @click.stop.prevent
      >
        <OptionsButton
          size="xs"
          color="nav"
          class="invisible group-hover/navItem:visible"
          data-testid="team-dropdown-button"
        />
      </OptionsDropdown>
    </UButton>
  </div>
</template>
